/**
 * URL Codec Service
 * 
 * Handles URL encoding and decoding using native browser APIs.
 * Single Responsibility: Core encoding/decoding logic only.
 * 
 * POODR Principles:
 * - Single Responsibility: Only encoding/decoding, no UI
 * - Explicit Interface: encode() and decode() methods
 * - Dependency Injection: Native APIs passed via constructor for testability
 */

type EncodingType = 'uriComponent' | 'wholeUri';
type ConversionMode = 'encode' | 'decode';

interface CodecResult {
  success: boolean;
  result: string;
  error?: string;
}

interface UrlCodecConfig {
  encodeURIComponent?: typeof encodeURIComponent;
  decodeURIComponent?: typeof decodeURIComponent;
  encodeURI?: typeof encodeURI;
  decodeURI?: typeof decodeURI;
}

/**
 * URL Codec - Core encoding/decoding service
 * 
 * Uses native browser APIs:
 * - encodeURIComponent: Encodes all special characters (for query params)
 * - encodeURI: Preserves URL structural chars (for full URLs)
 */
class UrlCodec {
  private encodeURIComponentFn: typeof encodeURIComponent;
  private decodeURIComponentFn: typeof decodeURIComponent;
  private encodeURIFn: typeof encodeURI;
  private decodeURIFn: typeof decodeURI;

  constructor(config: UrlCodecConfig = {}) {
    this.encodeURIComponentFn = config.encodeURIComponent ?? encodeURIComponent;
    this.decodeURIComponentFn = config.decodeURIComponent ?? decodeURIComponent;
    this.encodeURIFn = config.encodeURI ?? encodeURI;
    this.decodeURIFn = config.decodeURI ?? decodeURI;
  }

  /**
   * Encode text to URL-safe format
   */
  encode(text: string, type: EncodingType): CodecResult {
    if (!text) {
      return { success: true, result: '' };
    }

    try {
      const result = type === 'uriComponent'
        ? this.encodeURIComponentFn(text)
        : this.encodeURIFn(text);
      
      return { success: true, result };
    } catch (error) {
      return {
        success: false,
        result: '',
        error: error instanceof Error ? error.message : 'Encoding failed'
      };
    }
  }

  /**
   * Decode URL-encoded text back to plain text
   */
  decode(text: string, type: EncodingType): CodecResult {
    if (!text) {
      return { success: true, result: '' };
    }

    try {
      const result = type === 'uriComponent'
        ? this.decodeURIComponentFn(text)
        : this.decodeURIFn(text);
      
      return { success: true, result };
    } catch (error) {
      // Provide user-friendly error messages for common cases
      let errorMessage = 'Invalid encoded string';
      
      if (error instanceof Error) {
        if (error.message.includes('URI malformed')) {
          errorMessage = 'Malformed URL encoding - check for incomplete % sequences';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        result: '',
        error: errorMessage
      };
    }
  }

  /**
   * Process text based on mode and type
   */
  process(text: string, mode: ConversionMode, type: EncodingType): CodecResult {
    return mode === 'encode'
      ? this.encode(text, type)
      : this.decode(text, type);
  }
}

export { UrlCodec };
export type { EncodingType, ConversionMode, CodecResult, UrlCodecConfig };
